import React, { useState, useRef, useContext, useEffect } from "react";
import { PlusCircle, Trash2 } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "./Navbar";
import axios from "axios";
import languageContext from "../context/language/languageContext";
import Select from "react-select";

export default function PrescriptionForm() {
  const [patientName, setPatientName] = useState("");
  const { setAlert, setMessage, setSessionTimeout } = useContext(languageContext);
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [nextAppointment, setNextAppointment] = useState("");
  const [symptoms, setSymptoms] = useState("");
  const [diagnosis, setDiagnosis] = useState("");
  const [medicines, setMedicines] = useState([
    { name: "", dosage: "", times: [""], days: "", reminderDays: [], frequency: "" }
  ]);
  const [labTests, setLabTests] = useState([{ name: "", bookingDate: "" }]);
  const [sos, setSOS] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [pharmacyOptions, setPharmacyOptions] = useState([]);
  const [labOptions, setLabOptions] = useState([]);
  const [selectedPharmacy, setSelectedPharmacy] = useState(null);
  const [selectedLab, setSelectedLab] = useState(null);

  const pdfRef = useRef();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    fetchPharmacyOptions();
    fetchLabOptions();
    const patient_name = location.state?.patient_name;
    setPatientName(patient_name)
  }, []);




  const fetchPharmacyOptions = async () => {
    try {
      const response = await axios.get("https://www.careplus.gopillz.com/new_add-chemist", {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
      });
      setPharmacyOptions(response.data.chemist_list.map((item) => ({ label: item.chemist_name, value: item.id })));
    } catch (error) {
      console.error("Error fetching pharmacies:", error);
    }
  };

  const fetchLabOptions = async () => {
    try {
      const response = await axios.get("https://www.careplus.gopillz.com/new_add-lab", {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
      });
      setLabOptions(response.data.labs.map((item) => ({ label: item.lab_name, value: item.id })));
    } catch (error) {
      console.error("Error fetching labs:", error);
    }
  };

  const handleAddMedicine = () =>
    setMedicines([...medicines, { name: "", dosage: "", times: [""], days: "", reminderDays: [], frequency: "" }]);

  const handleRemoveMedicine = (index) =>
    setMedicines(medicines.filter((_, i) => i !== index));

  const handleMedicineChange = (index, field, value) => {
    const updated = [...medicines];
    if (field === "frequency") {
      const freq = parseInt(value) || 0;
      updated[index][field] = value;
      updated[index]["times"] = Array(freq).fill("");
    } else {
      updated[index][field] = value;
    }
    setMedicines(updated);
  };

  const handleTimeChange = (medIndex, timeIndex, value) => {
    const updated = [...medicines];
    updated[medIndex].times[timeIndex] = value;
    setMedicines(updated);
  };

  const handleAddLabTest = () =>
    setLabTests([...labTests, { name: "", bookingDate: "" }]);

  const handleRemoveLabTest = (index) =>
    setLabTests(labTests.filter((_, i) => i !== index));

  const handleLabTestChange = (index, field, value) => {
    const updated = [...labTests];
    updated[index][field] = value;
    setLabTests(updated);
  };

  const formatTimeTo12Hour = (time24) => {
    if (!time24) return "";
    const [hourStr, minute] = time24.split(":");
    let hour = parseInt(hourStr, 10);
    const ampm = hour >= 12 ? "PM" : "AM";
    hour = hour % 12 || 12;
    return `${hour}:${minute} ${ampm}`;
  };

  const shouldShowPharmacy = medicines.some(med => med.name || med.dosage || med.times?.length > 0 || med.days);
  const shouldShowLabSelect = labTests.some(test => test.name.trim() !== "");

  const handleSubmit = async () => {
    const patient_id = location.state?.patient_id;
    if (!patient_id) return;
    setLoading(true);

    const formattedMedicines = medicines.map(med => ({
      ...med,
      times: med.times.map(time => formatTimeTo12Hour(time))
    }));

    const formattedLabTests = labTests.map(test => ({
      name: test.name,
      bookingDate: test.bookingDate
    }));

    try {
      const response = await axios.post(
        "https://www.careplus.gopillz.com/generate_prescription",
        JSON.stringify({
          id: patient_id,
          patient_name: patientName,
          symptoms,
          diagnosis,
          nextAppointment,
          medicines: formattedMedicines,
          lab_tests: formattedLabTests,
          sos,
          notes,
          pharmacy_id: selectedPharmacy?.value,
          lab_id: selectedLab?.value
        }),
        {
          headers: {
            Accept: "*/*",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json"
          }
        }
      );

      if (response.status === 200) {
        setMessage(response.data.chat_history);
        navigate("/doctor-dashboard");
      }
    } catch (error) {
      if (error.response?.data?.detail === "Token has expired") {
        setSessionTimeout(true);
      } else {
        console.error("Error:", error.message);
        setAlert(true);
        setMessage("Please try again later, something went wrong");
      }
    } finally {
      setLoading(false);
    }
  };

  const dayOptions = [
    { value: "Everyday", label: "Everyday" },
    { value: "Mon", label: "Mon" },
    { value: "Tues", label: "Tues" },
    { value: "Wed", label: "Wed" },
    { value: "Thru", label: "Thru" },
    { value: "Fri", label: "Fri" },
    { value: "Sat", label: "Sat" },
    { value: "Sun", label: "Sun" }
  ];

  return (
    <div className="h-screen overflow-hidden flex flex-col">
      <Navbar />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex-grow pt-24 overflow-y-auto"
      >
        <div
          ref={pdfRef}
          className="max-w-5xl mx-auto bg-white rounded-2xl shadow-lg p-4 sm:p-6 space-y-4 border border-blue-100"
          style={{ minHeight: "calc(100vh - 6rem)", overflowY: "auto" }}
        >
          <h1 className="text-2xl sm:text-3xl font-bold text-blue-700 text-center">
            🩺 Digital Prescription
          </h1>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="w-full mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                👤 Patient Name
              </label>
              <div className="w-full rounded-lg border border-gray-300 bg-gray-100 px-3 py-2 text-sm text-gray-700">
                {patientName || "No patient name"}
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700">Date</label>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400" />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="text-sm font-semibold text-gray-700">🩹 Symptoms</label>
              <textarea rows={3} value={symptoms} onChange={(e) => setSymptoms(e.target.value)} placeholder="Describe symptoms" className="mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400" />
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700">🧠 Diagnosis</label>
              <textarea rows={3} value={diagnosis} onChange={(e) => setDiagnosis(e.target.value)} placeholder="Diagnosis details" className="mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400" />
            </div>
          </div>

          <div className="bg-blue-50 rounded-2xl p-3 sm:p-4">
            {/* <h2 className="font-semibold text-blue-700 text-lg mb-3">💊 Medicines</h2> */}
            <div className="bg-blue-50 rounded-2xl p-4 sm:p-6">
              <h2 className="font-semibold text-blue-700 text-lg mb-4 flex items-center">
                💊 <span className="ml-2">Medicines</span>
              </h2>

              {medicines.map((med, index) => (
                <div
                  key={index}
                  className="bg-white p-4 rounded-xl border border-blue-100 mb-4 shadow-sm space-y-4"
                >
                  <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Medicine Name</label>
                      <input
                        type="text"
                        placeholder="e.g. Paracetamol"
                        value={med.name}
                        onChange={(e) => handleMedicineChange(index, "name", e.target.value)}
                        className="w-full rounded-md border px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Dosage</label>
                      <input
                        type="text"
                        placeholder="500mg"
                        value={med.dosage}
                        onChange={(e) => handleMedicineChange(index, "dosage", e.target.value)}
                        className="w-full rounded-md border px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Days</label>
                      <input
                        type="number"
                        placeholder="5"
                        value={med.days}
                        onChange={(e) => handleMedicineChange(index, "days", e.target.value)}
                        className="w-full rounded-md border px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Frequency (times/day)</label>
                      <input
                        type="number"
                        min="1"
                        placeholder="e.g. 2"
                        value={med.frequency}
                        onChange={(e) => handleMedicineChange(index, "frequency", e.target.value)}
                        className="w-full rounded-md border px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400"
                      />
                    </div>
                  </div>

                  {med.frequency > 0 && (
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Set Times</label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        {med.times.map((time, timeIndex) => (
                          <input
                            key={timeIndex}
                            type="time"
                            value={time}
                            onChange={(e) => handleTimeChange(index, timeIndex, e.target.value)}
                            className="rounded-md border px-2 py-2 text-sm focus:ring-2 focus:ring-blue-400"
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Reminder Days</label>
                    <Select
                      isMulti
                      options={dayOptions}
                      value={dayOptions.filter((option) =>
                        med.reminderDays.includes(option.value)
                      )}
                      onChange={(selectedOptions) =>
                        handleMedicineChange(
                          index,
                          "reminderDays",
                          selectedOptions.map((option) => option.value)
                        )
                      }
                      placeholder="Select Days"
                      className="w-full text-sm"
                    />
                  </div>

                  <div className="flex justify-end">
                    <button
                      onClick={() => handleRemoveMedicine(index)}
                      className="flex items-center text-red-500 hover:text-red-700 text-sm"
                    >
                      <Trash2 size={18} className="mr-1" /> Remove Medicine
                    </button>
                  </div>
                </div>
              ))}

              <button
                onClick={handleAddMedicine}
                className="flex items-center mt-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                <PlusCircle size={18} className="mr-1" /> Add Medicine
              </button>
            </div>

          </div>

          <div className="bg-blue-50 rounded-2xl p-3 sm:p-4">
            <h2 className="font-semibold text-blue-700 text-lg mb-3">🧪 Lab Tests</h2>
            {labTests.map((test, index) => (
              <div key={index} className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-center mb-2">
                <input type="text" placeholder="Test Name" value={test.name} onChange={(e) => handleLabTestChange(index, "name", e.target.value)} className="rounded-md border px-2 py-1 text-sm" />
                <input type="date" value={test.bookingDate} onChange={(e) => handleLabTestChange(index, "bookingDate", e.target.value)} className="rounded-md border px-2 py-1 text-sm" />
                <button onClick={() => handleRemoveLabTest(index)} className="text-red-500 hover:text-red-600">
                  <Trash2 size={18} />
                </button>
              </div>
            ))}
            <button onClick={handleAddLabTest} className="flex items-center mt-2 text-blue-600 hover:text-blue-800 text-sm">
              <PlusCircle size={18} className="mr-1" /> Add Lab Test
            </button>
          </div>

          <div className="bg-blue-50 rounded-2xl p-3 sm:p-4">
            <h2 className="font-semibold text-blue-700 text-lg mb-3">📅 Follow-Up Appointment</h2>
            <input type="datetime-local" value={nextAppointment} onChange={(e) => setNextAppointment(e.target.value)} className="mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400" />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="text-sm font-semibold text-gray-700">🚨 SOS Instructions</label>
              <textarea rows={3} value={sos} onChange={(e) => setSOS(e.target.value)} placeholder="In case of emergency..." className="mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400" />
            </div>
            <div>
              <label className="text-sm font-semibold text-gray-700">📝 Additional Notes</label>
              <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Doctor’s additional advice" className="mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400" />
            </div>
          </div>

          {(shouldShowPharmacy || shouldShowLabSelect) && (
            <div className="grid gap-4 sm:grid-cols-2">
              {shouldShowPharmacy && (
                <div>
                  <label className="text-sm font-semibold text-gray-700">🏪 Choose Pharmacy</label>
                  <Select options={pharmacyOptions} value={selectedPharmacy} onChange={setSelectedPharmacy} placeholder="Select pharmacy" className="mt-1" />
                </div>
              )}
              {shouldShowLabSelect && (
                <div>
                  <label className="text-sm font-semibold text-gray-700">🔬 Choose Lab</label>
                  <Select options={labOptions} value={selectedLab} onChange={setSelectedLab} placeholder="Select lab" className="mt-1" />
                </div>
              )}
            </div>
          )}

          <button
            onClick={handleSubmit}
            disabled={loading}
            className={`w-full py-3 rounded-xl flex justify-center items-center gap-2 transition font-semibold ${loading ? "bg-blue-300 cursor-not-allowed text-white" : "bg-blue-600 hover:bg-blue-700 text-white"}`}
          >
            {loading ? (
              <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
              </svg>
            ) : (
              <><PlusCircle size={20} /> Save</>
            )}
          </button>
        </div>
      </motion.div>
    </div>
  );
}
